<template>
  <div class="font-sans text-grey-1 w-full mx-auto md:max-w-lg">
    <transition name="fade" mode="out-in">
      <div v-if="showForm">
        <FormulateForm
          #default="{ isLoading }"
          :key="key"
          v-model="values"
          name="estimateRequestForm"
          @submit="submit"
        >
          <div class="p-8 sm:p-16 shadow-xl rounded-lg">
            <fieldset v-show="showNicheSelection" class="mt-4">
              <legend class="tg-mobile-label md:tg-desktop-label text-grey-1">
                {{ $t('my_building_is') }}
              </legend>
              <div class="flex flex-col sm:flex-row gap-4 mt-4">
                <div
                  v-for="option in nicheOptions"
                  :key="option.id"
                  class="w-full"
                >
                  <input
                    :id="option.id"
                    v-model="values.niche"
                    :value="option.value"
                    type="radio"
                    class="peer sr-only"
                  />
                  <label
                    class="flex justify-center flex-wrap bg-white border border-gray-300 rounded text-grey-2 text-sm cursor-pointer focus:outline-none hover:bg-gray-50 md:py-4 peer-checked:text-black peer-checked:border-black peer-checked:font-bold w-full py-2"
                    :for="option.id"
                  >
                    <div class="md:w-full text-center">
                      <font-awesome-icon
                        :icon="['far', option.icon]"
                        class="md:text-2xl md:mb-4 mr-2"
                      />
                    </div>
                    <span class="text-center">{{ option.label }}</span>
                  </label>
                </div>
              </div>
            </fieldset>

            <div class="my-8">
              <button
                type="button"
                :title="$t('property_additional_information')"
                class="text-xs sm:text-base text-decorative-link text-cherry uppercase font-semibold block"
                @click="showAdditionalInformation = !showAdditionalInformation"
              >
                <font-awesome-icon
                  :icon="['fas', showAdditionalInformation ? 'minus' : 'plus']"
                  class="mr-1.5 fa-xs"
                />
                {{ $t('property_additional_information') }}
              </button>

              <transition name="fade" mode="out-in">
                <div v-if="showAdditionalInformation" class="my-4">
                  <label
                    class="tg-mobile-label md:tg-desktop-label text-grey-1"
                  >
                    {{ $t('address') }}
                  </label>
                  <FormulateInput
                    type="text"
                    name="street"
                    :label="$t('street')"
                    :placeholder="$t('street')"
                    outer-class="mt-2 mb-4"
                  />
                  <FormulateInput
                    type="text"
                    name="number"
                    :label="$t('house_number')"
                    :placeholder="$t('house_number')"
                  />

                  <template v-if="!commercialNicheSelected">
                    <label
                      for="number_of_bedrooms"
                      class="font-bold text-sm text-tg-color"
                    >
                      {{ $t('num_bedrooms') }}
                    </label>
                    <FormulateInput
                      id="number_of_bedrooms"
                      type="number"
                      name="number_of_bedrooms"
                      min="0"
                      value="0"
                      validation="bail|min:0|max:100"
                      :validation-name="$t('num_bedrooms')"
                      :input-class="['rounded-tr-none rounded-br-none']"
                      :element-class="['flex']"
                      outer-class="mt-1 mb-4 max-w-[180px]"
                    >
                      <template #suffix>
                        <div class="input-unit input-unit-black">
                          <font-awesome-icon :icon="['far', 'bed']" />
                        </div>
                      </template>
                    </FormulateInput>
                  </template>

                  <label
                    for="surface_livable"
                    class="font-bold text-sm text-tg-color"
                  >
                    {{
                      $t(
                        commercialNicheSelected
                          ? 'surface_trading'
                          : 'surface_livable'
                      )
                    }}
                  </label>
                  <FormulateInput
                    :id="
                      commercialNicheSelected
                        ? 'surface_trading'
                        : 'surface_livable'
                    "
                    type="number"
                    :name="
                      commercialNicheSelected
                        ? 'surface_trading'
                        : 'surface_livable'
                    "
                    min="0"
                    value="0"
                    validation="min:0"
                    :validation-name="
                      $t(
                        commercialNicheSelected
                          ? 'surface_trading'
                          : 'surface_livable'
                      )
                    "
                    :input-class="['rounded-tr-none rounded-br-none']"
                    :element-class="['flex']"
                    outer-class="mt-1 mb-4 max-w-[180px]"
                  >
                    <template #suffix>
                      <div class="input-unit input-unit-black">
                        m²
                      </div>
                    </template>
                  </FormulateInput>

                  <template v-if="!commercialNicheSelected">
                    <label class="my-4 font-bold text-sm text-tg-color">
                      {{ $t('details') }}
                    </label>
                    <FormulateInput
                      type="checkbox"
                      name="has_terrace"
                      :label="$t('terrace')"
                      outer-class="mt-2"
                    />
                    <FormulateInput
                      type="checkbox"
                      name="has_garage"
                      :label="$t('garage')"
                      outer-class="mt-2"
                    />
                    <FormulateInput
                      type="checkbox"
                      name="has_garden"
                      :label="$t('garden')"
                      outer-class="mt-2"
                    />
                  </template>
                </div>
              </transition>
            </div>

            <FormulateInput
              type="email"
              name="email"
              validation="bail|required|email"
              :label="$t('email_address')"
              :placeholder="getRequiredPlaceholder('email_address')"
              :show-required-label="true"
            />
            <FormulateInput
              type="tel"
              name="mobile"
              :placeholder="$t('telephone_number')"
              :label="$t('telephone_number')"
              :show-optional-label="true"
            />
            <FormulateInput
              type="text"
              name="first_name"
              validation="required:trim"
              :label="$t('first_name')"
              :placeholder="getRequiredPlaceholder('first_name')"
              :show-required-label="true"
            />
            <FormulateInput
              type="text"
              name="last_name"
              validation="required:trim"
              :label="$t('last_name')"
              :placeholder="getRequiredPlaceholder('last_name')"
              :show-required-label="true"
            />

            <FormulateInput
              type="autocomplete"
              auto-complete-type="city"
              name="city"
              validation="required"
              :label="$t('city')"
              :placeholder="getRequiredPlaceholder('city_placeholder')"
              :show-required-label="true"
            />

            <FormulateInput
              v-model="showCommentField"
              :options="{ '1': $t('yes'), '0': $t('no') }"
              type="select"
              :label="$t('would_you_like_to_add_additional_information')"
            />
            <transition name="fade" mode="out-in">
              <FormulateInput
                v-show="showCommentField === '1'"
                name="comment"
                type="textarea"
                :label="$t('additional_information_optional')"
                :placeholder="$t('additional_information_placeholder')"
              />
            </transition>

            <FormulateErrors class="my-3.75 text-center" />

            <p class="tg-body-extra-small text-grey-2 text-center">
              {{ $t('privacy_policy_optional') }}
              <a
                target="_blank"
                :href="$t('privacy_policy_url')"
                class="font-bold underline text-cherry inline-block"
              >
                {{ $t('privacy_policy') }}.
              </a>
              {{ $t('consult_our') }}
              <a
                target="_blank"
                :href="$t('privacy_policy_url')"
                class="font-bold underline text-cherry"
              >
                {{ $t('privacy_policy') }}</a
              >
              {{ $t('for_more_information') }}
            </p>
          </div>

          <div class="flex justify-center">
            <FormulateInput
              id="schatting-submit"
              type="submit"
              :disabled="isLoading"
              :input-class="['w-full shadow-lg']"
              class="mt-8 w-1/2"
            >
              <font-awesome-icon
                v-if="isLoading"
                :icon="['far', 'spinner-third']"
                spin
                class="mr-2"
              />
              {{ $t('estimate_request_submit') }}
            </FormulateInput>
          </div>
        </FormulateForm>
      </div>
    </transition>
  </div>
</template>

<script>
import { createEstimateRequest } from '@/services/apiService';
import { NICHE_RESIDENTIAL, NICHE_COMMERCIAL } from '@/forms/niches';
import {
  getRequiredPlaceholder,
  getUrlParam,
  recaptchaSubmit
} from '@/utils/helpers';

export default {
  name: 'EstimateRequest',
  props: {
    niche: {
      type: String
    }
  },
  data() {
    return {
      key: Math.random(),
      values: {
        niche: NICHE_RESIDENTIAL
      },
      showForm: true,
      showCommentField: '0',
      showNicheSelection: true,
      isLuxury: false,
      adMeta: {},
      showAdditionalInformation: false
    };
  },
  computed: {
    nicheOptions() {
      return [
        {
          value: NICHE_RESIDENTIAL,
          label: this.$t('residential_real_estate'),
          icon: 'fa-house',
          id: 'niche_1'
        },
        {
          value: NICHE_COMMERCIAL,
          label: this.$t('commercial_real_estate'),
          icon: 'fa-industry',
          id: 'niche_2'
        }
      ];
    },
    commercialNicheSelected() {
      return parseInt(this.values.niche) === NICHE_COMMERCIAL;
    }
  },
  created() {
    this.init();
  },
  methods: {
    getRequiredPlaceholder,
    init() {
      this.setAdMeta();
      const niche = this.niche;
      if (!niche) return;
      this.showNicheSelection = false;
      // Default selected niche is RESIDENTIAL
      if (niche === 'commercial') {
        this.$set(this.values, 'niche', NICHE_COMMERCIAL);
      } else if (niche === 'luxury') {
        this.isLuxury = true;
      }
    },
    setAdMeta() {
      let result = {};
      const fbclid = getUrlParam('fbclid');
      const gclid = getUrlParam('gclid');
      if (fbclid) {
        result = { fbclid: fbclid };
      } else if (gclid) {
        result = { gclid: gclid };
      }
      this.adMeta = result;
    },
    async submit(payload) {
      try {
        const reCaptcha = await recaptchaSubmit();
        // Make sure that comment field is cleared
        // should the user set showCommentField
        // to "No" again after filling out text.
        if (this.showCommentField === '0') {
          payload.comment = '';
        }
        payload.city = payload.city.id;
        payload.is_luxury = this.isLuxury;
        payload.ad_meta = this.adMeta;
        const response = await createEstimateRequest(payload, reCaptcha);
        location.href = this.getRedirectUrl();
        return response;
      } catch (error) {
        this.$formulate.handle(error, 'estimateRequestForm');
      }
    },
    getRedirectUrl() {
      const NICHE_RESIDENTIAL_ENDPOINT_MAPPING = {
        nl: 'succes-schatting-woonvastgoed',
        fr: 'succes-demande-d-estimation-immobilier-residentiel',
        en: 'success-estimation-residential-property'
      };

      const NICHE_COMMERCIAL_ENDPOINT_MAPPING = {
        nl: 'succes-schatting-bedrijfsvastgoed',
        fr: 'succes-demande-d-estimation-immobilier-entreprise',
        en: 'success-estimation-commercial-property'
      };

      const NICHE_LUXURY_ENDPOINT_MAPPING = {
        nl: 'succes-schatting-luxevastgoed',
        fr: 'succes-demande-d-estimation-immobilier-de-luxe',
        en: 'success-estimation-luxury-property'
      };

      const language = this.$i18n.locale;
      let endpoint =
        this.values.niche === NICHE_RESIDENTIAL
          ? NICHE_RESIDENTIAL_ENDPOINT_MAPPING[language]
          : NICHE_COMMERCIAL_ENDPOINT_MAPPING[language];

      if (this.isLuxury) {
        endpoint = NICHE_LUXURY_ENDPOINT_MAPPING[language];
      }

      return `${location.origin}/${language}/${endpoint}`;
    }
  }
};
</script>

<style src="@/styles/main.css" />
